import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subscription } from 'rxjs'
import { SmsaSettings, SmsaSettingsService } from './smsa-settings.service'
import { getCityOptions, getCountryOptions, MAJOR_SA_CITIES } from '../shared/origin-location.util'

const SMSA_CREDENTIAL_FIELDS: (keyof SmsaSettings)[] = ['passKey']

// Mirrors SMSA_SUPPORTED_COUNTRIES in stacks-agents'
// src/app/shipping/providers/smsa/smsa.provider.ts.
const SMSA_SUPPORTED_COUNTRIES = ['SA', 'AE', 'BH', 'KW', 'OM', 'QA', 'JO']

@Component({
  selector: 'app-smsa-provider',
  templateUrl: './smsa-provider.component.html',
  styleUrls: ['../../../integrations/integrations.component.scss', '../shipping-providers-theme.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SmsaProviderComponent implements OnInit, OnDestroy {
  @Input() projectId!: string
  @Input() isOpen = false
  @Output() openPanel = new EventEmitter<void>()
  @Output() closePanel = new EventEmitter<void>()
  @Output() saved = new EventEmitter<void>()

  settings: SmsaSettings = {
    enabled: false,
    passKey: '',
    originCountryCode: 'SA',
    originCityName: '',
    originAddressLine: '',
    originPhone: '',
    flatRate: null,
    baseRate: null,
    perKgRate: null,
    codEnabled: false,
  }

  countries = getCountryOptions(SMSA_SUPPORTED_COUNTRIES)

  originCities: string[] = []

  private statusSub?: Subscription

  constructor(public readonly service: SmsaSettingsService) {
    // originCountryCode defaults to 'SA' above, so the city list must be
    // seeded immediately — not just after an existing connection loads.
    this.refreshOriginCities()
  }

  ngOnInit(): void {
    this.statusSub = this.service.status$.subscribe((status) => {
      if (!status?.connected) return
      this.settings.enabled = status.enabled
      this.settings.originCountryCode = status.originCountryCode ?? this.settings.originCountryCode
      this.settings.originCityName = status.originCityName ?? this.settings.originCityName
      this.settings.originAddressLine = status.originAddressLine ?? this.settings.originAddressLine
      this.settings.originPhone = status.originPhone ?? this.settings.originPhone
      this.settings.flatRate = status.flatRate ?? null
      this.settings.baseRate = status.baseRate ?? null
      this.settings.perKgRate = status.perKgRate ?? null
      this.settings.codEnabled = status.codEnabled ?? this.settings.codEnabled
      this.service.maskCredentials(this.settings, SMSA_CREDENTIAL_FIELDS)
      this.refreshOriginCities()
    })
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe()
  }

  /**
   * Repopulates the Origin City options for the currently selected origin
   * country. Keeps an already-saved city selected even if it isn't in the
   * list, so switching countries never silently drops existing data.
   */
  onOriginCountryChange(): void {
    this.settings.originCityName = ''
    this.refreshOriginCities()
  }

  private refreshOriginCities(): void {
    const countryIsoCode = this.settings.originCountryCode
    const curated = countryIsoCode === 'SA' ? MAJOR_SA_CITIES : undefined
    this.originCities = getCityOptions(countryIsoCode, this.settings.originCityName, curated)
  }

  /**
   * Saves encrypted SMSA configuration through stacks-agents.
   * Strips placeholder values so the backend preserves existing credentials.
   */
  async save(): Promise<void> {
    const settingsToSave = { ...this.settings }
    for (const field of SMSA_CREDENTIAL_FIELDS) {
      if (settingsToSave[field] === this.service.CREDENTIAL_PLACEHOLDER) {
        ;(settingsToSave as Record<string, unknown>)[field] = ''
      }
    }
    await this.service.save(this.projectId, settingsToSave)
    this.saved.emit()
  }

  /**
   * Removes the SMSA integration through stacks-agents while keeping
   * credential handling inside the backend.
   */
  disconnect(): void {
    this.service.disconnect(this.projectId)
  }
}
